
import React, { useRef, useEffect, useState } from 'react';
import { Language } from '../types';
import { SearchIcon, CameraIcon } from './icons';
import { t } from '../translations';
import { toast } from 'sonner';

interface SearchHeaderProps {
    language: Language;
    searchQuery: string;
    setSearchQuery: (query: string) => void;
    onVisualSearch: (imageBase64: string) => void;
    isAiLoading: boolean;
}

const SearchHeader: React.FC<SearchHeaderProps> = ({ 
    language, 
    searchQuery, 
    setSearchQuery, 
    onVisualSearch,
    isAiLoading,
}) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    // "/" hotkey — фокусирует search input. Стандартная конвенция: GitHub, Linear, Notion.
    // Игнорируем когда уже в input/textarea (не дублировать).
    useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
            if (e.metaKey || e.ctrlKey || e.altKey) return;
            if (e.key === '/') {
                e.preventDefault();
                inputRef.current?.focus();
                inputRef.current?.select();
            }
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, []);

    // Rotating placeholder: 4 варианта сменяются каждые 3 секунды пока input пустой.
    // Прекращается после фокуса/ввода, чтобы не отвлекать гостя при наборе.
    const placeholders = [
        t('search.placeholder', language),
        t('search.placeholderRotate1', language),
        t('search.placeholderRotate2', language),
        t('search.placeholderRotate3', language),
    ];
    const [phIndex, setPhIndex] = useState(0);
    const [focused, setFocused] = useState(false);
    useEffect(() => {
        if (focused || searchQuery) return;
        const id = setInterval(() => setPhIndex((i) => (i + 1) % placeholders.length), 3000);
        return () => clearInterval(id);
    }, [focused, searchQuery, language]);

    /**
     * Снимок с телефона весит мегабайты, а на сервер уходит в base64 (плюс треть).
     * Раньше отправляли как есть — запрос не проходил вовсе, поиск по фото не
     * работал ни с одной настоящей фотографией. Ужимаем до 1024px и JPEG:
     * блюдо на снимке распознаётся и в таком размере.
     */
    const downscale = (file: File): Promise<string> =>
        new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onerror = () => reject(new Error('read failed'));
            reader.onload = () => {
                const img = new Image();
                img.onerror = () => reject(new Error('decode failed'));
                img.onload = () => {
                    const MAX = 1024;
                    const scale = Math.min(1, MAX / Math.max(img.width, img.height));
                    const canvas = document.createElement('canvas');
                    canvas.width = Math.round(img.width * scale);
                    canvas.height = Math.round(img.height * scale);
                    const ctx = canvas.getContext('2d');
                    if (!ctx) return reject(new Error('no canvas'));
                    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
                    resolve(canvas.toDataURL('image/jpeg', 0.8).split(',')[1]);
                };
                img.src = reader.result as string;
            };
            reader.readAsDataURL(file);
        });

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;
        try {
            onVisualSearch(await downscale(file));
        } catch {
            // Сжать не вышло (необычный формат). Мелкий файл отправим как есть,
            // а крупный отбросит nginx — гость получил бы невнятную ошибку,
            // поэтому говорим прямо.
            if (file.size > 800 * 1024) {
                toast.error(t('search.photoTooBig', language));
            } else {
                const reader = new FileReader();
                reader.onload = (e) => onVisualSearch((e.target?.result as string).split(',')[1]);
                reader.readAsDataURL(file);
            }
        } finally {
            // Иначе повторный выбор того же файла не вызовет change.
            event.target.value = '';
        }
    };

    return (
        <div className="mb-6">
            <div className="relative group">
                <div className="relative flex items-center bg-[var(--bg-tertiary-50)] border border-[var(--glass-border)] rounded-full shadow-sm group-focus-within:shadow-md group-focus-within:border-[var(--accent-20)] group-focus-within:bg-[var(--bg-card)] transition-all duration-300">
                    <div className="pl-5 text-[var(--text-secondary)] group-focus-within:text-[var(--accent-color)] transition-colors">
                        <SearchIcon className="w-5 h-5" />
                    </div>
                    <input
                        ref={inputRef}
                        type="text"
                        placeholder={placeholders[phIndex]}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onFocus={() => setFocused(true)}
                        onBlur={() => setFocused(false)}
                        className="w-full px-4 py-3.5 bg-transparent text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none text-base font-medium transition-[placeholder] duration-300"
                    />
                    <input type="file" ref={fileInputRef} onChange={handleFileChange} accept="image/*" className="hidden" />
                    <button
                        onClick={() => fileInputRef.current?.click()}
                        disabled={isAiLoading}
                        /* title даёт нативный tooltip на десктопе при hover.
                           На мобиле при long-press тоже. aria-label для screen-readers. */
                        title={t('search.visualSearch', language)}
                        aria-label={t('search.visualSearch', language)}
                        className="mr-2 p-3 rounded-2xl hover:bg-[var(--bg-tertiary)] transition-colors disabled:opacity-50 group/btn"
                    >
                        {isAiLoading ? (
                            <div className="w-5 h-5 animate-spin rounded-full border-2 border-[var(--accent-color)] border-t-transparent"></div>
                        ) : (
                            <CameraIcon className="w-5 h-5 text-[var(--text-secondary)] group-hover/btn:text-[var(--accent-color)] transition-colors" />
                        )}
                    </button>
                </div>
            </div>
        </div>
    )
}

export default SearchHeader;
